/**
 * Single API root for all backend calls.
 * - Production / CI: set VITE_API_BASE_URL at build time (e.g. https://your-api.example.net/api, no trailing slash).
 * - Local dev: unset → defaults to http://localhost:5101/api when `vite` runs in dev mode.
 */
function apiRoot(): string {
  const fromEnv = (import.meta.env.VITE_API_BASE_URL ?? '').trim();
  if (fromEnv) {
    return fromEnv.replace(/\/+$/, '');
  }
  if (import.meta.env.DEV) {
    return 'http://localhost:5101/api'.replace(/\/+$/, '');
  }
  console.error(
    '[Procure Flo] VITE_API_BASE_URL was not set at build time; set it in CI (e.g. static web app workflow env).'
  );
  return '';
}

const root = apiRoot();

export const API_BASE_URL = `${root}/requests`;

/** GET — aggregate counts per status (same scope as list). */
export const REQUEST_STATUS_COUNTS_URL = `${API_BASE_URL}/status-counts`;

export const DEFAULT_LIST_PAGE_SIZE = 20;

/** Options shown in grid “rows per page” selectors (current size is always valid). */
export const LIST_PAGE_SIZE_OPTIONS = [10, 20, 25, 50, 100] as const;

export const AUTH_BASE_URL = `${root}/auth`;

/** GET — current user profile, regions, and UI entitlement hints (JWT still authorizes API calls). */
export const AUTH_ME_URL = `${AUTH_BASE_URL}/me`;

/** Administrator APIs — user profiles and region master data. */
export const ADMIN_USER_PROFILES_URL = `${root}/admin/user-profiles`;
export const ADMIN_REGIONS_URL = `${root}/admin/regions`;
export const ADMIN_ROLES_URL = `${root}/admin/roles`;
export const ADMIN_PERMISSIONS_URL = `${root}/admin/permissions`;
/** GET/PUT — transaction configuration: request ID and PO number templates (Administrator only). */
export const ADMIN_NUMBERING_URL = `${root}/admin/numbering-configuration`;
/** GET/PUT — system-wide options (e.g. email notifications, app URL for email links). */
export const ADMIN_SYSTEM_CONFIGURATION_URL = `${root}/admin/system-configuration`;
/** GET — merged default + stored material SKU patterns (any authenticated user). */
export const MATERIAL_SKU_CONFIGURATION_URL = `${root}/material-sku-configuration`;
/** PUT — update material SKU patterns (Administrator only). */
export const ADMIN_MATERIAL_SKU_CONFIGURATION_URL = `${root}/admin/material-sku-configuration`;
export const PO_REGISTRY_URL = `${root}/purchase-orders`;

/** GET/POST — paged inventory items; GET /lookup for cascading dropdown data. */
export const INVENTORY_API_URL = `${root}/inventory`;

/** POST — on-hand stock grouped by yard/delivery for request-line SKUs. */
export const INVENTORY_AVAILABILITY_URL = `${INVENTORY_API_URL}/availability`;

/** GET/POST — material transfer forms (draft → submit → approve/plan → complete). */
export const MATERIAL_TRANSFERS_API_URL = `${root}/material-transfers`;

/** GET — transfers that fulfill a procurement request (full or partial). */
export function materialTransfersByRequestUrl(requestId: string): string {
  return `${MATERIAL_TRANSFERS_API_URL}/by-request/${encodeURIComponent(requestId)}`;
}

export const WELLS_API_URL = `${root}/wells`;

export const MASTER_API_URL = `${root}/master`;

/** GET /api/master/wells — books well file used by the request picker. */
export const MASTER_WELLS_API_URL = `${MASTER_API_URL}/wells`;

/** GET/POST /api/master/products — hydrocarbon catalog (OIL/GAS). */
export const PRODUCTS_API_URL = `${MASTER_API_URL}/products`;

/** GET/POST /api/contracts; GET/PUT /api/contracts/{id}; POST .../activate */
export const CONTRACTS_API_URL = `${root}/contracts`;

/** GET/POST /api/periods; PUT /api/periods/{id} */
export const PERIODS_API_URL = `${root}/periods`;

/** POST /api/excalibur/ingest/* ; GET /api/excalibur/compare */
export const EXCALIBUR_API_URL = `${root}/excalibur`;

/** GET /api/search?q= */
export const SEARCH_API_URL = `${root}/search`;

/** GET/POST /api/doi/decks; PUT /api/doi/decks/{id}; POST .../post */
export const DOI_DECKS_API_URL = `${root}/doi/decks`;

export const VOLUME_RUNS_API_URL = `${root}/volume-runs`;
export const SALES_INVOICES_API_URL = `${root}/sales-invoices`;
export const ROYALTY_PAYABLES_API_URL = `${root}/royalty-payables`;
export const PURCHASE_INVOICES_API_URL = `${root}/purchase-invoices`;
export const JIB_API_URL = `${root}/jib`;
export const ACCOUNTS_API_URL = `${root}/accounts`;
export const AFES_API_URL = `${root}/afes`;
export const INDEXES_API_URL = `${root}/indexes`;
export const PPA_API_URL = `${root}/ppa`;

/** GET — active regions for dropdowns (same shape as admin regions; public read). */
export const REGIONS_LIST_URL = `${root}/regions`;

/** GET — paginated wells (query: page, pageSize, region). */
export const WELLS_PAGE_URL = `${root}/wells/page`;

/** GET — PO spend time series for a well (query: granularity = yearly | monthly | quarterly). */
export function wellPoSpendUrl(wellId: string, granularity: string): string {
  const g = encodeURIComponent(granularity);
  return `${WELLS_API_URL}/${encodeURIComponent(wellId)}/po-spend?granularity=${g}`;
}

/** GET — MongoDB aggregate counts by state (region); empty state → Unspecified. */
export const WELLS_REGION_COUNTS_URL = `${root}/wells/region-counts`;

export const VENDORS_API_URL = `${root}/vendors`;

/** GET — paginated vendors (query: page, pageSize). */
export const VENDORS_PAGE_URL = `${root}/vendors/page`;

/** Request for quote (sourcing) — ProcurementStaff. */
export const RFQ_API_URL = `${root}/rfq`;

/** GET/POST /api/agent/conversations; POST .../{id}/messages */
export const AGENT_CONVERSATIONS_URL = `${root}/agent/conversations`;

export function agentQuoteDecisionUrl(rfqId: string): string {
  return `${root}/agent/quote-drafts/${encodeURIComponent(rfqId)}/decision`;
}

/** GET /api/procurement-search — history register. Books DOI search stays on /api/search. */
export const PROCUREMENT_SEARCH_URL = `${root}/procurement-search`;

/** GET — aggregate counts per RFQ status (same region scope as list). */
export const RFQ_STATUS_COUNTS_URL = `${RFQ_API_URL}/status-counts`;

/** Operations approval queue and approve/reject (OperationsManager / Administrator). */
export const OPERATIONS_RFQ_API_URL = `${root}/operations/rfq`;

/** GET — counts for pending / approved / rejected RFQs (operations queue). */
export const OPERATIONS_RFQ_STATUS_COUNTS_URL = `${OPERATIONS_RFQ_API_URL}/status-counts`;

/** GET — PDF attachment for a vendor quote (Bearer). */
export function rfqVendorAttachmentUrl(rfqId: string, vendorId: string): string {
  return `${RFQ_API_URL}/${encodeURIComponent(rfqId)}/vendors/${encodeURIComponent(vendorId)}/attachment`;
}

/** Anonymous vendor quote form — GET/POST /api/public/rfq/{token}. */
export const PUBLIC_VENDOR_RFQ_URL = `${root}/public/rfq`;

export const DELIVERY_ADDRESSES_API_URL = `${root}/delivery-addresses`;

/** GET — aggregate PO counts by status (created / paid). */
export const PO_STATUS_COUNTS_URL = `${PO_REGISTRY_URL}/status-counts`;

export const PRODUCT_NAME = 'Procure Flo';

/** Background refresh for request list + status tiles, and PO list + tiles when on that tab. */
export const UI_POLL_INTERVAL_MS = 60_000;

export function poApiUrl(requestId: string): string {
  return `${root}/requests/${requestId}/purchase-orders`;
}

/** GET — application/pdf (same authorization as GET /api/requests/{id}). */
export function requestPdfUrl(requestId: string): string {
  return `${root}/requests/${encodeURIComponent(requestId)}/pdf`;
}

/** GET — application/pdf (ProcurementStaff; same scope as GET /api/purchase-orders/{id}). */
export function purchaseOrderPdfUrl(purchaseOrderId: string): string {
  return `${root}/purchase-orders/${encodeURIComponent(purchaseOrderId)}/pdf`;
}

/** GET — application/pdf (same authorization as GET /api/material-transfers/{id}). */
export function materialTransferPdfUrl(materialTransferId: string): string {
  return `${root}/material-transfers/${encodeURIComponent(materialTransferId)}/pdf`;
}

/** GET — movement ticket / receipt attached when a planned transfer is completed. */
export function materialTransferReceiptUrl(materialTransferId: string): string {
  return `${root}/material-transfers/${encodeURIComponent(materialTransferId)}/receipt`;
}
